// © 2025 Camp Promethean, LLC. All Rights Reserved.
// Selection Dialog Component

import { useState } from 'react';
import { EnhancedSelection, mapSelectionToEnhanced } from '@/types/selection';
import { Plant } from '@/types/plant';
import { SensoryData } from '@/types/sensory';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { SelectionForm } from './SelectionForm';
import { createSelection, updateSelection } from '@/lib/api/selections';

interface SelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selection?: EnhancedSelection;
  plant: Plant;
  sensoryData?: SensoryData;
  onSuccess?: (selection: EnhancedSelection) => void;
  projectId: string;
}

export function SelectionDialog({
  isOpen,
  onClose,
  selection,
  plant,
  sensoryData,
  onSuccess,
  projectId
}: SelectionDialogProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const isEditing = !!selection;
  
  // Handle form submission
  const handleSubmit = async (formData: EnhancedSelection) => {
    try {
      setIsLoading(true);
      
      // Prepare data for API (remove client-side only fields)
      const apiData = {
        id: formData.id,
        pheno_id: formData.phenoId,
        plant_id: formData.plantId,
        generation: formData.generation,
        selection_date: formData.selectionDate,
        selection_rationale: formData.selectionRationale,
        selection_notes: formData.selectionNotes,
        selection_highlights: formData.selectionHighlights,
        growth_metrics: {
          structure: formData.growthMetrics.structure,
          vigor: formData.growthMetrics.vigor,
          yield: formData.growthMetrics.yield,
          frost: formData.growthMetrics.frost,
          overall: formData.growthMetrics.overall
        },
        tags: formData.tags.map(tag => ({
          id: tag.id,
          name: tag.name,
          category: tag.category
        })),
        project_id: projectId
      };
      
      // Submit to API
      let result;
      if (isEditing) {
        result = await updateSelection(apiData);
      } else {
        result = await createSelection(apiData);
      }
      
      // Transform result to enhanced selection
      const enhancedResult = mapSelectionToEnhanced(
        result,
        formData.sensoryData,
        formData.tags,
        formData.badges
      );
      
      // Show success toast
      toast({
        title: isEditing ? 'Selection Updated' : 'Selection Created',
        description: isEditing 
          ? `Successfully updated selection for ${formData.plantId}` 
          : `Successfully created new selection for ${formData.plantId}`,
        variant: 'default',
      });
      
      // Close dialog and pass result to success handler
      onClose();
      if (onSuccess) {
        onSuccess(enhancedResult);
      }
    } catch (error) {
      console.error('Error saving selection:', error);
      toast({
        title: 'Error',
        description: `Failed to ${isEditing ? 'update' : 'create'} selection. Please try again.`,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? `Edit Selection: ${selection.plantId}` : 'Create New Selection'}
          </DialogTitle>
          <DialogDescription>
            {isEditing 
              ? 'Update the details for this selection.' 
              : 'Select this plant for its outstanding characteristics.'}
          </DialogDescription>
        </DialogHeader>
        
        <SelectionForm
          selection={selection}
          plant={plant}
          sensoryData={sensoryData}
          onSubmit={handleSubmit}
          onCancel={onClose}
          isLoading={isLoading}
        />
      </DialogContent>
    </Dialog>
  );
} 